<template>
  <!-- eslint-disable prefer-standard-components/use-standard-components -->
  <v-btn
    @click="handleClick"
    v-bind="bindProps"
    :append-icon="buttonProps.appendIcon"
    :aria-disabled="ariaDisabled || undefined"
    :aria-label="useAriaLabel"
    :block="fullWidth"
    :class="[buttonProps.customClasses, { 'standard-button--full-width': fullWidth }]"
    :color="buttonProps.color"
    :data-analytics-properties="stringifyAnalyticsProperties(localAnalyticsProperties)"
    :data-cy="dataTestid"
    :data-prevent-double-click="preventDoubleClick"
    :data-testid="dataTestid"
    :disabled="disabled"
    :href="localHref"
    :icon="iconOnly"
    :loading="loading"
    :min-height="minHeight"
    :min-width="layoutMinWidth"
    :rounded="rounded"
    :size="buttonProps.size"
    :to="localTo"
    :variant="buttonProps.variant"
  >
    <template
      v-if="buttonProps.prependIcon"
      #prepend
    >
      <v-icon
        :class="{ 'fs-20': buttonProps.iconSize === 'small' }"
        :icon="buttonProps.prependIcon"
        class="material-icons-outlined"
      />
    </template>

    <span v-if="!iconOnly && text">{{ textTranslate ? $bct(text) : text }}</span>

    <v-icon
      v-if="iconOnly && icon"
      :icon="icon"
      :size="iconSize"
      class="material-icons-outlined"
    />

    <slot v-else-if="!text" />
  </v-btn>
  <!-- eslint-enable prefer-standard-components/use-standard-components -->
</template>

<script setup>
// Docs: https://bridgecare.atlassian.net/wiki/spaces/DS/pages/2476638214/Buttons
import _ from 'lodash'; // eslint-disable-line id-length

import { stringifyAnalyticsProperties } from '@/shared/composables/analytics/useAnalyticsProperties';
import useEventBus from '@/shared/composables/useEventBus';

const $bct = inject('translator');
const eventBus = useEventBus();

const emit = defineEmits(['click']);

const props = defineProps({
  // "Template" for the button styling based on function/role
  role: {
    type: String,
    default: null,
  },
  // Primary props
  appendIcon: {
    type: String,
    default: null,
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  bindProps: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  disabledText: {
    type: String,
    default: null,
  },
  disabledTextTranslate: {
    type: Boolean,
    default: true,
  },
  text: {
    type: String,
    default: '',
  },
  textTranslate: {
    type: Boolean,
    default: true,
  },
  icon: {
    type: String,
    default: null,
  },
  // default in Vuetify translates to 24px
  iconSize: {
    type: [String, Number],
    default: null,
  },
  prependIcon: {
    type: String,
    default: null,
  },
  preventDoubleClick: {
    type: Boolean,
    default: true,
  },
  suppressParentClick: {
    type: Boolean,
    default: false,
  },
  languageSelect: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  analyticsProperties: {
    type: Object,
    default: null,
  },
  dataTestid: {
    type: String,
    default: null,
  },
  // Other styling
  color: {
    type: String,
    default: null,
  },
  customClasses: {
    type: [String, Array, Object],
    default: '',
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: null,
  },
  variant: {
    type: String,
    default: null,
  },
  rounded: {
    type: Boolean,
    default: null,
  },
  href: {
    type: String,
    default: undefined,
  },
  // eslint-disable-next-line id-length -- aligns with vue-router / VBtn `to` prop
  to: {
    type: [String, Object],
    default: undefined,
  },
});

// Validate that prependIcon and appendIcon are not both provided
watchEffect(() => {
  if (props.prependIcon && props.appendIcon && !props.languageSelect) {
    const errorMessage = 'StandardButton: May have an icon prepended or appended, but not both.';
    console.error(errorMessage);
  }
});

// WCAG 2.1 AAA compliance for mobile target size (min-width skipped when fullWidth so :block can span the row)
const layoutMinWidth = computed(() => (props.fullWidth ? undefined : '3rem'));
const minHeight = '3rem';

const ariaDisabled = computed(() => Boolean(props.disabledText) || props.disabled || props.loading);

const iconOnly = computed(() => !props.text && !!props.icon);

const localTo = computed(() => (ariaDisabled.value ? undefined : props.to));

const localHref = computed(() => (ariaDisabled.value ? undefined : props.href));

const roleDefaults = computed(() => {
  const defaults = {
    // Basic set of roles
    secondary: {
      variant: 'outlined',
      classes: 'bg-surface',
    },
    destructive: {
      color: 'error',
      variant: 'outlined',
    },
    link: {
      variant: 'text',
      size: 'small',
    },

    // Additional roles
    create: {
      prependIcon: 'add',
      iconSize: 'small',
    },
  };

  return props.role ? defaults[props.role] || {} : {};
});

// This is where we include any defaults that would traditionally be defined at the prop level
// (the default button aligns with our "primary" button styling/display)
const buttonProps = computed(() => {
  const appendIconValue = props.appendIcon || roleDefaults.value.appendIcon;
  const prependIconValue = props.prependIcon || roleDefaults.value.prependIcon;
  const resolvedColor = props.color ?? roleDefaults.value.color ?? 'primary';

  return {
    appendIcon: appendIconValue && !iconOnly.value ? appendIconValue : undefined,
    color: resolvedColor === '' ? undefined : resolvedColor,
    customClasses: [roleDefaults.value.classes, props.customClasses].filter(Boolean),
    iconSize: props.iconSize ?? roleDefaults.value.iconSize ?? 'default',
    prependIcon: prependIconValue && !iconOnly.value ? prependIconValue : undefined,
    size: props.size ?? roleDefaults.value.size ?? 'default',
    variant: props.variant ?? roleDefaults.value.variant ?? 'flat',
  };
});

const localAnalyticsProperties = computed(() => {
  const baseProperties = props.analyticsProperties || {};

  // If textTranslate is true and we have text, include the English text in analytics
  if (props.textTranslate && props.text) {
    return {
      ...baseProperties,
      text: props.text,
    };
  }

  return baseProperties;
});

const useAriaLabel = computed(() => {
  // This would be a rare edge case but output to browser that we need to fix this scenario
  if (iconOnly.value && !props.ariaLabel) {
    console.warn('Icon-only buttons require an aria-label for accessibility compliance');
  }

  if (props.ariaLabel) return $bct(props.ariaLabel);

  // Otherwise, return text. We don't need aria-label if we have text.
  return props.textTranslate ? $bct(props.text) : props.text;
});

// Use lodash debounce to prevent double clicks
// https://lodash.com/docs/4.17.15#debounce
const debouncedEmit = _.debounce((event) => emit('click', event), 1000, {
  leading: true,
  trailing: false,
});

function handleClick(event) {
  if (props.loading) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (event && typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    } else if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    return;
  }

  if (props.disabledText) {
    const blockedChimeMessage = props.disabledTextTranslate
      ? $bct(props.disabledText)
      : props.disabledText;
    event.preventDefault();
    event.stopImmediatePropagation();
    eventBus.chime(blockedChimeMessage);
    return;
  }

  if (props.disabled) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  if (props.suppressParentClick) {
    event.preventDefault();
    event.stopPropagation();
  }

  if (props.preventDoubleClick) {
    debouncedEmit(event);
  } else {
    emit('click', event);
  }
}
</script>

<style scoped>
/* Ensure minimum touch target size for WCAG compliance; is mobile enhancement
48px = 3 rem. Full-width buttons use Vuetify block width only — fixed min-width would override it. */
.v-btn:not(.standard-button--full-width) {
  min-width: 3rem !important;
}

.v-btn {
  min-height: 3rem !important;
}

/* Responsive sizing for different screen sizes */
@media (min-width: 48rem) {
  /* 768px */
  .v-btn:not(.standard-button--full-width) {
    min-width: 2.75rem !important;
  }

  .v-btn {
    min-height: 2.75rem !important;
  }
}

/* This adds invisible pseudo-element for larger touch target */
.v-btn::before {
  content: '';
  position: absolute;
  top: -0.5rem;
  left: -0.5rem;
  right: -0.5rem;
  bottom: -0.5rem;
}

/* This is an OS option for those with motion sensitivity; only impacts if they select this option */
@media (prefers-reduced-motion: reduce) {
  .v-btn {
    transition: none !important;
  }
}

.v-btn--size-x-large {
  font-size: 16px !important;
}
</style>
